import { PENDO_ENVIRONMENT_IDENTIFIER } from '@lyrahealth-inc/shared-app-logic'

import type { PendoAccount, PendoVisitor } from '@pendo/agent'

type PendoEnvironment = (typeof PENDO_ENVIRONMENT_IDENTIFIER)[keyof typeof PENDO_ENVIRONMENT_IDENTIFIER]

interface AppPendoVisitor extends PendoVisitor {
  environment: PendoEnvironment
}

interface InitializePendoConfig {
  visitor: AppPendoVisitor
  account?: PendoAccount
}

const PENDO_API_KEY = 'df6062b6-cc49-42c7-4ad1-2f64e138a021'
const PENDO_ENV = 'us1'

let pendoLoadPromise: Promise<void> | null = null
let isPendoLoaded = false

/**
 * Dynamically loads Pendo agent without blocking app initialization.
 * Uses dynamic import for code splitting and lazy loading.
 *
 * @returns Promise that resolves when Pendo is loaded and ready
 */
export const loadPendo = async (): Promise<void> => {
  // Return existing promise if already loading
  if (pendoLoadPromise) {
    return pendoLoadPromise
  }

  // Return immediately if already loaded
  if (isPendoLoaded) {
    return Promise.resolve()
  }

  pendoLoadPromise = (async () => {
    try {
      // Dynamic import for code splitting - won't be in main bundle
      const pendoModule = await import(
        /* webpackChunkName: "pendo-agent" */
        /* webpackPrefetch: true */
        '@pendo/agent'
      )

      // Map the ESM exports from @pendo/agent onto the global-style pendo object
      window.pendo = {
        initialize: pendoModule.initialize,
        identify: pendoModule.identify,
        track: pendoModule.track,
        clearSession: pendoModule.clearSession,
        validateInstall: pendoModule.validateInstall,
        validateEnvironment: pendoModule.validateEnvironment,
        pageLoad: pendoModule.pageLoad,
      }

      isPendoLoaded = true
    } catch (error) {
      console.error('Failed to load Pendo script:', error)
    }
  })()

  return pendoLoadPromise
}

/**
 * Initialize Pendo with user and account data.
 * Automatically loads Pendo if not already loaded.
 */
export const initializePendo = async (config: InitializePendoConfig): Promise<void> => {
  await loadPendo()

  try {
    window.pendo?.initialize?.({
      apiKey: PENDO_API_KEY,
      env: PENDO_ENV,
      ...config,
    })
  } catch (error) {
    console.error('Failed to initialize Pendo:', error)
  }
}

/**
 * Clear Pendo session on logout
 */
export const clearPendoSession = () => {
  // Only try to clear if Pendo is already loaded
  if (isPendoLoaded) {
    window.pendo?.clearSession?.()
  }
}

/**
 * Check if Pendo is properly installed (for ad-blocker detection)
 */
export const validatePendoInstall = (): boolean => {
  if (!isPendoLoaded) return false
  return window.pendo?.validateInstall?.(true) !== undefined
}

/**
 * Track a Pendo event.
 * Automatically loads Pendo if not already loaded.
 */
export const trackPendoEvent = async (eventName: string, properties?: any): Promise<void> => {
  await loadPendo()
  window.pendo?.track?.(eventName, properties)
}
